import type {
  LeafConditionStyle,
  LeafConditionValue,
  LeafFilterCondition,
} from './describe-filter-condition'

import { formatBoolean } from '@/utils/presentation/formatting'
import { referenceDataTransformationConfig } from '@/utils/presentation/instrument-data-transforms'

import { describeFilterCondition } from './describe-filter-condition'

const ELLIPSIS = '…'
const DEFAULT_MAX_DEPTH = 10

type ValueFormatter = (value: LeafConditionValue) => string

export interface DescribeFilterOptions {
  style: LeafConditionStyle
  labelField: (field: string) => string
  getValueFormatter?: (field: string) => ValueFormatter | undefined
  resolveListName?: (id: string) => string
  maxDepth?: number
  /** Clauses rendered per level before an ellipsis; unbounded by default. */
  maxParts?: number
  parenthesizeOrBranch?: boolean
  /** What a walk past `maxDepth` yields; the grid shows an ellipsis, prose shows nothing. */
  depthOverflowText?: string
}

/**
 * One top-level clause of a filter, rendered to text. An `or`/`not` subtree is a single
 * clause with no `field` and no operators, and its `text` already carries any parentheses.
 */
interface FilterClause {
  text: string
  field: string | null
  operatorCount: number
}

interface WalkClause extends FilterClause {
  disjunctive: boolean
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isBooleanField(field: string): boolean {
  const config =
    referenceDataTransformationConfig[
      field as keyof typeof referenceDataTransformationConfig
    ]
  return config?.valueTransformation === formatBoolean
}

// Only a plain equality against a boolean reads as prose; any other operator keeps
// its rendering rather than being silently swallowed.
function describeBooleanCondition(
  label: string,
  condition: LeafFilterCondition,
): string | null {
  const operators = Object.entries(condition)
  if (operators.length === 0) return null
  for (const [operator, value] of operators) {
    if (operator !== 'equal_to' && operator !== 'not_equal_to') return null
    if (typeof value !== 'boolean') return null
  }
  if (condition.equal_to === false || condition.not_equal_to === true) {
    return `not ${label}`
  }
  return label
}

function joinBounded(
  items: string[],
  separator: string,
  maxParts: number,
): string {
  const shown = items.slice(0, maxParts)
  if (items.length > maxParts) shown.push(ELLIPSIS)
  return shown.join(separator)
}

function walkClauses(
  filter: unknown,
  options: DescribeFilterOptions,
  depth: number,
): WalkClause[] {
  const {
    style,
    labelField,
    getValueFormatter,
    resolveListName,
    maxDepth = DEFAULT_MAX_DEPTH,
    maxParts = Number.POSITIVE_INFINITY,
    parenthesizeOrBranch = false,
    depthOverflowText = ELLIPSIS,
  } = options
  if (!isPlainObject(filter)) return []
  if (depth > maxDepth) {
    return [
      {
        text: depthOverflowText,
        field: null,
        operatorCount: 0,
        disjunctive: false,
      },
    ]
  }

  const clauses: WalkClause[] = []

  for (const key of Object.keys(filter)) {
    const value = filter[key]
    if (value == null) continue

    // Flattened here so a disjunction nested under it stays parenthesizable by the caller's join.
    if (key === 'and') {
      const branches = Array.isArray(value) ? value : []
      for (const branch of branches) {
        clauses.push(...walkClauses(branch, options, depth + 1))
      }
      continue
    }

    if (key === 'or') {
      const branches = Array.isArray(value) ? value : []
      const nested = branches
        .map((branch) => describeFilter(branch, options, depth + 1))
        .filter(Boolean)
        .map((text) =>
          parenthesizeOrBranch && text.includes(' or ') ? `(${text})` : text,
        )
      if (nested.length === 0) continue
      clauses.push({
        text: joinBounded(nested, ' or ', maxParts),
        field: null,
        operatorCount: 0,
        disjunctive: nested.length > 1,
      })
      continue
    }

    if (key === 'not') {
      const nested = describeFilter(value, options, depth + 1)
      if (!nested) continue
      clauses.push({
        text: `not (${nested})`,
        field: null,
        operatorCount: 0,
        disjunctive: false,
      })
      continue
    }

    // Field key. On the wire a field key always maps to a flat leaf predicate;
    // nesting only ever happens through `and`/`or`/`not`.
    const label = labelField(key)
    if (!isPlainObject(value)) {
      clauses.push({
        text: label,
        field: key,
        operatorCount: 0,
        disjunctive: false,
      })
      continue
    }
    const condition = value as LeafFilterCondition
    const operatorCount = Object.keys(value).length
    const booleanText = isBooleanField(key)
      ? describeBooleanCondition(label, condition)
      : null
    if (booleanText !== null) {
      clauses.push({
        text: booleanText,
        field: key,
        operatorCount,
        disjunctive: false,
      })
      continue
    }
    const conditionText = describeFilterCondition(condition, {
      style,
      formatValue: getValueFormatter?.(key),
      resolveListName,
    })
    clauses.push({
      text: conditionText ? `${label} ${conditionText}` : label,
      field: key,
      operatorCount,
      disjunctive: false,
    })
  }

  return clauses
}

/**
 * Flattens a top-level `and` and expands every clause's sibling field keys, so
 * `{and: [{a}, {b, c}]}` yields three clauses. Never throws on an unexpected shape.
 */
export function describeFilterClauses(
  filter: unknown,
  options: DescribeFilterOptions,
  depth = 0,
): FilterClause[] {
  const clauses = walkClauses(filter, options, depth).filter(
    (clause) => clause.text !== '',
  )
  // Only a clause with siblings needs its parentheses.
  const parenthesize = clauses.length > 1
  return clauses.map(({ text, field, operatorCount, disjunctive }) => ({
    text: parenthesize && disjunctive ? `(${text})` : text,
    field,
    operatorCount,
  }))
}

/** Renders a filter tree to text, e.g. `Duration ≥ 1 and (Sector = A or Sector = B)`. */
export function describeFilter(
  filter: unknown,
  options: DescribeFilterOptions,
  depth = 0,
): string {
  return joinBounded(
    describeFilterClauses(filter, options, depth).map((clause) => clause.text),
    ' and ',
    options.maxParts ?? Number.POSITIVE_INFINITY,
  )
}
